import { Injectable } from '@angular/core';
import { doc, DocumentReference, Firestore, getDoc } from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { NavController } from '@ionic/angular';
import { IClubState, ITableState, ITournamentState, TableType } from '@soposopo/sopo-models';
import { firstValueFrom } from 'rxjs';
import { AuthStoreDispatcher } from '../core/state/auth/auth.dispatcher';
import { GameStoreDispatcher } from '../core/state/game/game.dispatcher';
import { GameToLoad } from '../core/state/game/game.state';
import { LoginAndRegistrationComponent } from '../shared/components/login-and-registration/login-and-registration.component';
import { SopoPopupService } from '../sopo-popup/service/sopo-popup.service';
import { SopoAlertSettings } from '../sopo-popup/interfaces/alert';
import { SopoConfirmSettings } from '../sopo-popup/interfaces/confirm';

@Injectable({
    providedIn: 'root'
})
export class NavigateFromLinkService {

    constructor(
        private authDispatcher: AuthStoreDispatcher,
        private gameDispatcher: GameStoreDispatcher,
        public navCtrl: NavController,
        private firestore: Firestore,
        public matDialog: MatDialog,
        private sopoPopupService: SopoPopupService,
    ) { }

    async navigateToTable(tableId: string) {
        const linkIsAvailable = await this.tableIsAvailable(tableId);
        if (!linkIsAvailable) {
            return this.showAlert({
                message: 'Table is not available anymore.',
            });
        }
        const isAuth = await firstValueFrom(this.authDispatcher.isAuthenticated());
        if (!isAuth) {
            const contineAsGuest = await this.decideAction();
            if (contineAsGuest === null) return;
            if (contineAsGuest) {
                await this.guestLogin();
                await this.goToTable(tableId);
                await this.navCtrl.navigateForward('login'); //in main window/tab the login page should be shown
            } else {
                const response = await this.showLoginPopup();
                if (response === 'accept') {
                    await this.goToTable(tableId);
                } else await this.navCtrl.navigateForward('login');
            }
        } else {
            await this.goToTable(tableId);
            const isAnonymous = await this.authDispatcher.isAnonymous();
            if (isAnonymous) await this.navCtrl.navigateForward('login'); //in main window/tab the login page should be shown
        }
    }

    async navigateToRegPage(tournamentId: string) {
        const linkIsAvailable = await this.tournamentIsAvailable(tournamentId);
        if (!linkIsAvailable) {
            return this.showAlert({
                message: 'Tournament is not available anymore.',
            });
        }
        const isAuth = await firstValueFrom(this.authDispatcher.isAuthenticated());
        if (!isAuth) {
            const contineAsGuest = await this.decideAction();
            if (contineAsGuest === null) return;
            if (contineAsGuest) {
                await this.guestLogin();
                await this.goToTournament(tournamentId);
            } else {
                const response = await this.showLoginPopup();
                if (response === 'accept') {
                    await this.goToTournament(tournamentId);
                } else await this.navCtrl.navigateForward('login');
            }
        } else await this.goToTournament(tournamentId);
    }

    async goToTable(tableId: string): Promise<void> {
        try {
            const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId: "SOPO", type: TableType.RING };
            await this.gameDispatcher.addNewGame(game, true);
        } catch (e) {
            this.showAlert({
                message: 'Quickplay is not available right now. Please try again later.',
            });
        }
    }

    async goToTournament(tournamentId: string): Promise<void>{
        try {
            await this.navCtrl.navigateForward(`tournament-registration/SOPO/${tournamentId}`);
        } catch (e) {
            await this.showAlert({
                message: 'Quickplay is not available right now. Please try again later.',
            });
        }
    }

    async decideAction(): Promise<boolean> {
        const options: SopoConfirmSettings = {
            data: {
                body: 'You are about to enter a quickplay table \n Would you like to continue as a guest \n or log into a SOPO account?',
                okText: 'Guest',
                cancelText: 'Login',
            },
            sCss: ['green']
        }
        return this.sopoPopupService.promptForConfirmation('app', options);
    }

    async tableIsAvailable(tableId: string) {
        const docReference = doc(this.firestore, `tables/${tableId}`) as DocumentReference<ITableState>;
        const table = await getDoc(docReference);
        if (table.data() !== undefined) {
            const clubID = table.data()!.clubID;
            const isDeleted = table.data()!.deleteTable;
            if (clubID === 'SOPO' && !isDeleted) return true;
        }
        return false;
    }

    async tournamentIsAvailable(tournamentId: string) {
        const docReference = doc(this.firestore, `tournaments/${tournamentId}`) as DocumentReference<ITournamentState>;
        const tournament = await getDoc(docReference);
        if (tournament.data() !== undefined) {
            const clubID = tournament.data()!.clubID;
            const isDeleted = tournament.data()!.isDeleted;
            if (clubID === 'SOPO' && !isDeleted) return true;
        }
        return false;
    }

    async checkIfTableIsAlreadyInClub(tableId: string): Promise<boolean> {
        const docReference = doc(this.firestore, `tables/${tableId}`) as DocumentReference<ITableState>;
        const table = await getDoc(docReference);
        if (table.data()) {
            const tableData = table.data();
            const clubId = tableData?.clubID;
            if (clubId && clubId !== "SOPO" && !tableData?.deleteTable) {
                try {
                    const isAuth = await firstValueFrom(this.authDispatcher.isAuthenticated());
                    const myId = await this.authDispatcher.getMyID();
                    const isAnonymousUser = await this.authDispatcher.isAnonymous();
                    if (isAuth && tableData.seats.includes(myId) && !isAnonymousUser) {
                        const docReference = doc(this.firestore, `clubs/${clubId}`) as DocumentReference<IClubState>;
                        const club = await getDoc(docReference);
                        if (club.data()) {
                            const clubData = club.data();
                            await this.resultPopup(`This invitation link is not available anymore. The owner of the table moved it to ${clubData.name} club. You will be navigated to the table inside the club.`);
                            const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId, type: TableType.RING };
                            await this.gameDispatcher.addNewGame(game, true);
                        }
                    } else if (isAuth && !tableData.seats.includes(myId) && !isAnonymousUser) {
                        await this.resultPopup(`This invitation link is not available anymore. You will be navigated to the main lobby.`);
                        this.navCtrl.navigateBack('/lobby');
                    } else if (!isAuth || isAnonymousUser) {
                        await this.resultPopup(`The owner of this table is adding this table to their club. You will be redirected to the login page where you can register or create a new quickplay table.`);
                        await this.navCtrl.navigateRoot('login');
                    }
                } catch (err) {
                    console.error(err);
                }
                return true;
            }
        }
        return false;
    }

    async resultPopup(text: string): Promise<boolean> {
        const options: SopoConfirmSettings = {
            data: {
                body: text,
                okText: 'OK',
            }
        }
        return await this.sopoPopupService.promptForConfirmation('app', options);
    }
    
    async showAlert(options: SopoAlertSettings) {
        options.dismissAfter = 5000;
        this.sopoPopupService.showAlert('app', options);
    }

    async guestLogin(): Promise<void> {
        const isAuth = await firstValueFrom(this.authDispatcher.isAuthenticated());
        if (isAuth) return;
        localStorage.setItem('startedLoginAsGuest', 'true');
        return this.authDispatcher.signInAnonymously();
    }

    async showLoginPopup(): Promise<string> {
        const regDialogRef = this.sopoPopupService.openDialog('app', LoginAndRegistrationComponent, {
            data: {
                isQuickplay: true,
                showLoginInfos: false,
                email: "",
                createTableOption: false,
                navigateFrom: true,
            },
            closeButton: true,
            height: '47em',
            sCss: ['sopo-popup-login-padding'],
        });

        return firstValueFrom(regDialogRef.afterClosed);
    }
}
